// A process run by a service organisation, where the evidence is someone else's report. Its period
// rarely matches ours, and the controls the report assumes we perform at our end are conditions
// pointing back at our own controls.
//
// Titles and taglines track the design document so a walkthrough can follow it section by section.
import {Scenario} from "../model";

const FY26 = {from: "2026-01-01", to: "2026-12-31"};

const conclusion = {
  outcome: "operated_effectively" as const,
  supportsReliance: true,
  justification:
    "The service organisation's SOC 1 Type 2 report carries an unqualified opinion and covers the " +
    "payroll processing relied upon to 30 September. The complementary user entity control it " +
    "assumes — our own review of the payroll output report — was tested and concluded effective. " +
    "The residual quarter is covered by the provider's bridge letter and inquiry confirming no " +
    "change to its control environment. The period is covered in full.",
};

export const outsourcedPayroll: Scenario = {
  id: "s4",
  section: "Scenario 4",
  title: "Outsourced payroll, SOC 1 Type 2",
  tagline: "others' work, rollforward, and CUECs",
  situation:
    "Payroll processing outsourced. The service organisation's SOC 1 Type 2 covers 1 Oct 2025 – " +
    "30 Sep 2026; our year end is 31 Dec 2026. The report lists complementary user entity controls, " +
    "one of which is our own control PAY-07.",
  focus: {control: "PAY-01", scope: "Global"},
  controls: [
    {
      ref: "PAY-01",
      title: "Outsourced payroll processing",
      assessment: "Financial Controls",
      scopes: [{scope: "Global", design: "effective", implementation: "effective", oe: conclusion}],
    },
    {
      ref: "PAY-07",
      title: "Payroll output review",
      assessment: "Financial Controls",
      scopes: [
        {
          scope: "Global",
          design: "effective",
          implementation: "effective",
          oe: {
            outcome: "operated_effectively",
            supportsReliance: true,
            justification: "Twelve monthly reviews of the payroll output report were tested with no exceptions.",
          },
        },
      ],
    },
  ],
  edges: [
    {from: "PAY-07", to: "PAY-01", provenance: "Complementary user entity control named in the SOC 1 Type 2 report"},
  ],
  oe: [
    {
      control: "PAY-01",
      scope: "Global",
      period: FY26,
      basis: [
        {
          id: "b1",
          status: "obtained" as const,
          span: {from: "2026-01-01", to: "2026-09-30"},
          evidence: {
            kind: "work_of_others",
            label: "SOC 1 Type 2",
            provider: "Provider Ltd",
            reportPeriod: {from: "2025-10-01", to: "2026-09-30"},
            opinion: "Unqualified — controls operated effectively throughout the period",
            citations: [
              {
                quote:
                  "In our opinion, in all material respects, the controls related to the control " +
                  "objectives stated in the description operated effectively throughout the period " +
                  "1 October 2025 to 30 September 2026.",
                source: "SOC1_ProviderLtd.pdf",
                locator: "Section II — Service auditor's opinion",
                context: "The opinion is unqualified and covers the full report period.",
              },
              {
                quote:
                  "Payroll input files received from user entities are validated against the agreed " +
                  "control totals before processing; exceptions are rejected and reported back.",
                source: "SOC1_ProviderLtd.pdf",
                locator: "Section IV — Control objective 3",
                context: "The processing control we are relying on for payroll completeness.",
              },
              {
                quote:
                  "User entities are responsible for reviewing the payroll output report for accuracy " +
                  "and completeness within five business days of receipt.",
                source: "SOC1_ProviderLtd.pdf",
                locator: "Section III — Complementary user entity controls",
                context: "The CUEC that PAY-07 satisfies at our end.",
              },
            ],
          },
          conditions: [
            {
              kind: "note",
              text: "Service auditor's opinion unqualified",
              met: true,
              assertedBy: "A. Okafor",
              assertedAt: "08 Jan 2027",
            },
            {
              kind: "note",
              text: "Report scope covers the payroll processing we rely on",
              met: true,
              assertedBy: "A. Okafor",
              assertedAt: "08 Jan 2027",
            },
            {kind: "control", control: "PAY-07", scope: "Global"},
          ],
        },
        {
          id: "b2",
          status: "obtained" as const,
          span: {from: "2026-10-01", to: "2026-12-31"},
          evidence: {
            kind: "work_of_others",
            label: "Bridge letter and inquiry of the provider",
            provider: "Provider Ltd",
            opinion: "No changes to the control environment since the report period",
            citations: [
              {
                quote:
                  "There have been no material changes to the controls described in our SOC 1 Type 2 " +
                  "report between 1 October 2026 and 31 December 2026.",
                source: "Bridge_letter_Dec26.pdf",
                locator: "Paragraph 2",
                context: "Covers the residual quarter between the report period and our year end.",
              },
            ],
          },
          conditions: [
            {
              kind: "note",
              text: "No changes to the provider's control environment per bridge letter",
              met: true,
              assertedBy: "S. Patel",
              assertedAt: "12 Jan 2027",
            },
          ],
        },
      ],
      conclusion,
    },
  ],
};
